// src/pages/DocumentsCertificates.js
import React, { useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Stack,
  Button,
  Chip,
} from "@mui/material";

import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import DescriptionIcon from "@mui/icons-material/Description";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import SecurityIcon from "@mui/icons-material/Security";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import PaidIcon from "@mui/icons-material/Paid";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import OwnershipCertificateModal from "./OwnershipCertificateModal";
import AssetOwnerImage from "./AssetOwnerImage";

const StatCard = ({ icon, title, value }) => (
  <Paper
    elevation={0}
    sx={{
      p: 3,
      borderRadius: 3,
      border: "1px solid #e5e7eb",
      boxShadow: "0px 16px 40px rgba(15,23,42,0.06)",
      bgcolor: "#ffffff",
      height: "100%",
    }}
  >
    <Stack spacing={2}>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: 2,
          bgcolor: "#f4f5fb",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="body2" sx={{ color: "#6b7280", mb: 0.5 }}>
          {title}
        </Typography>
        <Typography variant="h5" sx={{ fontWeight: 700, color: "#111827" }}>
          {value}
        </Typography>
      </Box>
    </Stack>
  </Paper>
);

const OwnershipCard = ({ item, onView }) => {
  const [hover, setHover] = useState(false);

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: "1px solid #e5e7eb",
        overflow: "hidden",
        position: "relative",
        transition: "0.3s",
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {/* Image */}
      <Box sx={{ position: "relative", height: 190 }}>
        <AssetOwnerImage
          src={item.imageUrl}
          alt={item.title}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />

        <Chip
          label="Verified"
          size="small"
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            bgcolor: "#22c55e",
            color: "#fff",
          }}
        />
      </Box>

      {/* Content */}
      <Box sx={{ p: 3 }}>
        <Typography sx={{ fontWeight: 700 }}>{item.title}</Typography>
        <Typography variant="caption" sx={{ color: "#6b7280" }}>
          ID: {item.id}
        </Typography>
      </Box>

      {/* Hover Buttons */}
      {item.hasActions && (
        <Box
          sx={{
            position: "absolute",
            bottom: hover ? 0 : -65,
            left: 0,
            width: "100%",
            bgcolor: "#ffffff",
            borderTop: "1px solid #e5e7eb",
            p: 2,
            gap: 1.5,
            display: "flex",
            transition: ".3s ease",
          }}
        >
          {/* FIXED: View now calls parent handler with item */}
          <Button
            fullWidth
            sx={{ borderRadius: 999 }}
            onClick={() => onView(item)}
          >
            View
          </Button>

          <Button fullWidth sx={{ borderRadius: 999 }}>
            PDF
          </Button>
          <Button fullWidth sx={{ borderRadius: 999 }}>
            Verify
          </Button>
        </Box>
      )}
    </Paper>
  );
};

const ReceiptRow = ({ item, highlighted }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      mb: 2,
      borderRadius: 3,
      border: highlighted ? "1px solid #22c55e" : "1px solid #e5e7eb",
      boxShadow: "0px 10px 25px rgba(15,23,42,0.04)",
      bgcolor: "#ffffff",
    }}
  >
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      justifyContent="space-between"
    >
      <Stack direction="row" spacing={2} alignItems="center">
        <Box
          sx={{
            width: 60,
            height: 60,
            borderRadius: 2,
            overflow: "hidden",
            bgcolor: "#f3f4f6",
            flexShrink: 0,
          }}
        >
          <AssetOwnerImage
            src={item.imageUrl}
            alt={item.title}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        </Box>
        <Box>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: "#111827" }}
          >
            {item.title}
          </Typography>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{ mt: 0.5 }}
          >
            <Chip
              label={item.category}
              size="small"
              sx={{ borderRadius: 999, bgcolor: "#f3f4f6", fontSize: 11 }}
            />
            <Typography variant="caption" sx={{ color: "#9ca3af" }}>
              {item.escrowId}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
            <AccessTimeIcon sx={{ fontSize: 14, color: "#9ca3af" }} />
            <Typography variant="caption" sx={{ color: "#6b7280" }}>
              {item.date}
            </Typography>
          </Stack>
        </Box>
      </Stack>

      <Stack direction="row" spacing={4} alignItems="center">
        <Typography variant="body1" sx={{ fontWeight: 700, color: "#111827" }}>
          {item.amount}
        </Typography>
        <Chip
          label="Confirmed"
          size="small"
          sx={{
            bgcolor: "#ecfdf3",
            color: "#16a34a",
            borderRadius: 999,
            fontSize: 11,
          }}
        />
        <Button
          variant="outlined"
          size="small"
          startIcon={<CloudDownloadOutlinedIcon />}
          sx={{
            textTransform: "none",
            borderRadius: 999,
            fontSize: 12,
          }}
        >
          Download
        </Button>
      </Stack>
    </Stack>
  </Paper>
);

const NoticeRow = ({ item }) => (
  <Box
    sx={{
      borderLeft: "2px solid #0ea5e9",
      pl: 3,
      py: 2,
      "&:first-of-type": { pt: 0 },
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
      <Typography variant="body2" sx={{ fontWeight: 600, color: "#111827" }}>
        {item.title}
      </Typography>
      <Chip
        label={item.tag}
        size="small"
        sx={{
          borderRadius: 999,
          bgcolor: "#f3f4f6",
          fontSize: 11,
        }}
      />
    </Stack>
    <Typography
      variant="caption"
      sx={{ color: "#6b7280", display: "block", mb: 0.5 }}
    >
      {item.date}
    </Typography>
    <Typography variant="body2" sx={{ color: "#4b5563" }}>
      {item.description}
    </Typography>
  </Box>
);

// --- dummy data ---

const categories = [
  "All Categories",
  "Real Estate",
  "Commodities",
  "Arts",
  "Luxury",
  "Supply Chain",
  "Debt",
  "Islamic Finance",
  "Asset-Backed",
];

const ownershipCertificates = [
  {
    authority: "DLD",
    category: "Real Estate",
    title: "Downtown Dubai Tower",
    id: "DLD-2025-DB-001250",
    tokens: "1,250",
    ownership: "5%",
    issueDate: "20 Jun 2025",
    imageUrl:
      "https://images.pexels.com/photos/1680247/pexels-photo-1680247.jpeg?auto=compress&cs=tinysrgb&w=800",
    hasActions: true,
    multiAuthority: false,
  },
  {
    authority: "DLD",
    category: "Real Estate",
    title: "Marina Bay Residence",
    id: "DLD-2025-MB-002580",
    tokens: "2,500",
    ownership: "5%",
    issueDate: "25 Jul 2025",
    imageUrl:
      "https://images.pexels.com/photos/460672/pexels-photo-460672.jpeg?auto=compress&cs=tinysrgb&w=800",
    hasActions: true,
    multiAuthority: true,
  },
  {
    authority: "DMCC",
    category: "Commodities",
    title: "DMCC Gold Vault - Premium",
    id: "DMCC-2025-GLD-000375",
    tokens: "375",
    ownership: "0.75%",
    issueDate: "10 Aug 2025",
    imageUrl:
      "https://images.pexels.com/photos/210600/pexels-photo-210600.jpeg?auto=compress&cs=tinysrgb&w=800",
    hasActions: true,
    multiAuthority: false,
  },
  {
    authority: "VARA",
    category: "Financial Products",
    title: "MENA Tech Growth Fund",
    id: "VARA-2025-FIN-000080",
    tokens: "80",
    ownership: "0.8%",
    issueDate: "5 Sept 2025",
    imageUrl:
      "https://images.pexels.com/photos/4386324/pexels-photo-4386324.jpeg?auto=compress&cs=tinysrgb&w=800",
    hasActions: true,
    multiAuthority: false,
  },
  {
    authority: "DEWA",
    category: "Renewable Energy",
    title: "Mohammed bin Rashid Solar Park",
    id: "DEWA-2025-SOL-000020",
    tokens: "20",
    ownership: "0.02%",
    issueDate: "1 Oct 2025",
    imageUrl:
      "https://images.pexels.com/photos/987544/pexels-photo-987544.jpeg?auto=compress&cs=tinysrgb&w=800",
    hasActions: true,
    multiAuthority: false,
  },
  {
    authority: "ADAA",
    category: "Arts and collectibles",
    title: "Contemporary Art Collection - Series A",
    id: "ADAA-2025-ART-000002",
    tokens: "2",
    ownership: "2%",
    issueDate: "20 Oct 2025",
    imageUrl:
      "https://images.pexels.com/photos/3648850/pexels-photo-3648850.jpeg?auto=compress&cs=tinysrgb&w=800",
    hasActions: true,
    multiAuthority: false,
  },
  {
    authority: "SWISS",
    category: "Luxury goods",
    title: "Swiss Luxury Watch Portfolio",
    id: "LUX-2025-WATCH-012",
    tokens: "12",
    ownership: "10%",
    issueDate: "15 Apr 2025",
    imageUrl:
      "https://images.pexels.com/photos/190819/pexels-photo-190819.jpeg?auto=compress&cs=tinysrgb&w=800",
    hasActions: true,
    multiAuthority: false,
  },
  {
    authority: "ICC",
    category: "Supply Chain and Inventory",
    title: "Global Freight Container Pool",
    id: "SCM-2025-CONT-060",
    tokens: "60",
    ownership: "4%",
    issueDate: "22 Jun 2025",
    imageUrl:
      "https://images.pexels.com/photos/3057960/pexels-photo-3057960.jpeg?auto=compress&cs=tinysrgb&w=800",
    hasActions: true,
    multiAuthority: false,
  },
  {
    authority: "SEC",
    category: "Debt",
    title: "Corporate Bond Portfolio",
    id: "DEBT-2025-CORP-040",
    tokens: "40",
    ownership: "4%",
    issueDate: "5 Jul 2025",
    imageUrl:
      "https://images.pexels.com/photos/323705/pexels-photo-323705.jpeg?auto=compress&cs=tinysrgb&w=800",
    hasActions: true,
    multiAuthority: false,
  },
  {
    authority: "AAOIFI",
    category: "Islamic Finance",
    title: "Shariah-Compliant Property Fund",
    id: "ISLAMIC-2025-RE-040",
    tokens: "40",
    ownership: "5%",
    issueDate: "18 May 2025",
    imageUrl:
      "https://images.pexels.com/photos/1796715/pexels-photo-1796715.jpeg?auto=compress&cs=tinysrgb&w=800",
    hasActions: true,
    multiAuthority: false,
  },
  {
    authority: "SEC",
    category: "Asset-Backed Securities",
    title: "Equipment Lease ABS",
    id: "ABS-2025-EQUIP-030",
    tokens: "30",
    ownership: "4.3%",
    issueDate: "3 Aug 2025",
    imageUrl:
      "https://images.pexels.com/photos/4484078/pexels-photo-4484078.jpeg?auto=compress&cs=tinysrgb&w=800",
    hasActions: true,
    multiAuthority: false,
  },
];

const receipts = [
  {
    title: "Downtown Dubai Tower",
    category: "Real Estate",
    escrowId: "ESC-ZB-2025-001250",
    amount: "AED 125,000",
    date: "15 June 2025",
    imageUrl:
      "https://images.pexels.com/photos/1680247/pexels-photo-1680247.jpeg?auto=compress&cs=tinysrgb&w=800",
  },
  {
    title: "Marina Bay Residence",
    category: "Real Estate",
    escrowId: "ESC-ZB-2025-002580",
    amount: "AED 125,000",
    date: "20 July 2025",
    imageUrl:
      "https://images.pexels.com/photos/460672/pexels-photo-460672.jpeg?auto=compress&cs=tinysrgb&w=800",
  },
  {
    title: "DMCC Gold Vault - Premium",
    category: "Commodities",
    escrowId: "ESC-DMCC-2025-000375",
    amount: "AED 187,500",
    date: "5 August 2025",
    imageUrl:
      "https://images.pexels.com/photos/210600/pexels-photo-210600.jpeg?auto=compress&cs=tinysrgb&w=800",
  },
  {
    title: "MENA Tech Growth Fund",
    category: "Financial Products",
    escrowId: "ESC-VARA-2025-000080",
    amount: "AED 80,000",
    date: "1 September 2025",
    imageUrl:
      "https://images.pexels.com/photos/4386324/pexels-photo-4386324.jpeg?auto=compress&cs=tinysrgb&w=800",
  },
  {
    title: "Mohammed bin Rashid Solar Park",
    category: "Renewable Energy",
    escrowId: "ESC-DEWA-2025-000020",
    amount: "AED 100,000",
    date: "25 September 2025",
    imageUrl:
      "https://images.pexels.com/photos/987544/pexels-photo-987544.jpeg?auto=compress&cs=tinysrgb&w=800",
  },
  {
    title: "Contemporary Art Collection",
    category: "Arts and collectibles",
    escrowId: "ESC-ADAA-2025-000002",
    amount: "AED 50,000",
    date: "15 October 2025",
    imageUrl:
      "https://images.pexels.com/photos/3648850/pexels-photo-3648850.jpeg?auto=compress&cs=tinysrgb&w=800",
  },
  {
    title: "Swiss Luxury Watch Portfolio",
    category: "Luxury goods",
    escrowId: "ESC-SWISS-2025-000012",
    amount: "AED 30,000",
    date: "12 April 2025",
    imageUrl:
      "https://images.pexels.com/photos/190819/pexels-photo-190819.jpeg?auto=compress&cs=tinysrgb&w=800",
  },
  {
    title: "Global Freight Container Pool",
    category: "Supply Chain and Inventory",
    escrowId: "ESC-ICC-2025-000060",
    amount: "AED 12,000",
    date: "20 June 2025",
    imageUrl:
      "https://images.pexels.com/photos/3057960/pexels-photo-3057960.jpeg?auto=compress&cs=tinysrgb&w=800",
  },
  {
    title: "Corporate Bond Portfolio",
    category: "Debt",
    escrowId: "ESC-SEC-2025-000040",
    amount: "AED 16,000",
    date: "1 July 2025",
    imageUrl:
      "https://images.pexels.com/photos/323705/pexels-photo-323705.jpeg?auto=compress&cs=tinysrgb&w=800",
  },
  {
    title: "Shariah-Compliant Property Fund",
    category: "Islamic Finance",
    escrowId: "ESC-AAOIFI-2025-000040",
    amount: "AED 20,000",
    date: "15 May 2025",
    imageUrl:
      "https://images.pexels.com/photos/1796715/pexels-photo-1796715.jpeg?auto=compress&cs=tinysrgb&w=800",
  },
  {
    title: "Equipment Lease ABS",
    category: "Asset-Backed Securities",
    escrowId: "ESC-SEC-2025-000030",
    amount: "AED 15,000",
    date: "1 August 2025",
    imageUrl:
      "https://images.pexels.com/photos/4484078/pexels-photo-4484078.jpeg?auto=compress&cs=tinysrgb&w=800",
  },
];

const notices = [
  {
    title: "VARA Compliance Audit Complete",
    tag: "Audit",
    date: "15 October 2025",
    description:
      "Annual compliance audit successfully completed. All tokenization processes verified and approved across all asset categories.",
  },
  {
    title: "Multi-Authority Registry Update",
    tag: "Registry",
    date: "25 July 2025",
    description:
      "Asset ownership records updated across DLD, DMCC, DEWA, and VARA registries. All tokenized holdings synchronized.",
  },
  {
    title: "DMCC Commodities Verification",
    tag: "Verification",
    date: "12 August 2025",
    description:
      "Gold vault holdings verified and certified. Physical asset custody confirmed with blockchain records.",
  },
  {
    title: "Renewable Energy Asset Compliance",
    tag: "Audit",
    date: "30 September 2025",
    description:
      "DEWA solar park revenue distribution mechanisms audited and approved for Q4 2025.",
  },
];

const DocumentsCertificates = () => {
  const [activeCategory, setActiveCategory] = React.useState("All Categories");
  const [purchaseModalOpen, setPurchaseModalOpen] = React.useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const handleView = (item) => {
    setSelectedItem(item);
    setPurchaseModalOpen(true);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f3f4f6",
        py: 4,
        px: 2,
        display: "flex",
        justifyContent: "center",
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 1200 }}>
        {/* Back link */}
        <Button
          startIcon={<ArrowBackIosNewIcon sx={{ fontSize: 14 }} />}
          sx={{
            textTransform: "none",
            color: "#111827",
            mb: 2,
            px: 0,
            "&:hover": { backgroundColor: "transparent" },
          }}
        >
          Back to Dashboard
        </Button>

        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 50,
                height: 50,
                borderRadius: 3,
                bgcolor: "#ffffff",
                border: "1px solid #e5e7eb",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0px 10px 30px rgba(15,23,42,0.04)",
              }}
            >
              <DescriptionIcon sx={{ color: "#111827" }} />
            </Box>
            <Box>
              <Typography
                variant="h5"
                sx={{ fontWeight: 700, color: "#111827" }}
              >
                Documents &amp; Certificates
              </Typography>
              <Typography variant="body2" sx={{ color: "#6b7280" }}>
                Secure vault for verified legal documents
              </Typography>
            </Box>
          </Stack>

          <Chip
            icon={<SecurityIcon />}
            label="Blockchain secured"
            sx={{
              bgcolor: "#ecfdf3",
              color: "#16a34a",
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 600,
              "& .MuiChip-icon": {
                color: "#16a34a",
              },
            }}
          />
        </Box>

        {/* Stat cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <StatCard
              icon={<DescriptionIcon sx={{ color: "#6366f1" }} />}
              title="Certificates"
              value="11"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <StatCard
              icon={<ReceiptLongIcon sx={{ color: "#0ea5e9" }} />}
              title="Receipts"
              value="11"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <StatCard
              icon={<SecurityIcon sx={{ color: "#10b981" }} />}
              title="Authorities"
              value="5"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <StatCard
              icon={<ErrorOutlineIcon sx={{ color: "#f59e0b" }} />}
              title="Notices"
              value="4"
            />
          </Grid>
        </Grid>

        {/* Filter by Category */}
        <Paper
          elevation={0}
          sx={{
            p: 2.5,
            borderRadius: 3,
            border: "1px solid #e5e7eb",
            bgcolor: "#ffffff",
            mb: 3,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} mb={2}>
            <PaidIcon sx={{ fontSize: 18, color: "#6b7280" }} />
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: "#111827" }}
            >
              Filter by Category
            </Typography>
            <InfoOutlinedIcon sx={{ fontSize: 16, color: "#9ca3af" }} />
          </Stack>

          <Stack direction="row" spacing={1.5} flexWrap="wrap">
            {categories.map((c) => {
              const isActive = c === activeCategory;
              return (
                <Button
                  key={c}
                  onClick={() => setActiveCategory(c)}
                  variant={isActive ? "contained" : "text"}
                  sx={{
                    textTransform: "none",
                    borderRadius: 999,
                    fontSize: 13,
                    px: 2.5,
                    py: 0.75,
                    minHeight: 0,
                    ...(isActive
                      ? {
                          bgcolor: "#0284c7",
                          "&:hover": { bgcolor: "#0369a1" },
                        }
                      : {
                          color: "#4b5563",
                          "&:hover": { bgcolor: "#f3f4f6" },
                        }),
                  }}
                >
                  {c}
                </Button>
              );
            })}
          </Stack>
        </Paper>

        {/* Document security banner */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            border: "1px solid #bae6fd",
            bgcolor: "#e0f2fe",
            mb: 3,
          }}
        >
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                bgcolor: "#ffffff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <SecurityIcon sx={{ fontSize: 20, color: "#0284c7" }} />
            </Box>
            <Box>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "#0f172a", mb: 0.5 }}
              >
                Document Security &amp; Verification
              </Typography>
              <Typography variant="body2" sx={{ color: "#0f172a" }}>
                All documents are cryptographically signed on the blockchain and
                verified by regulatory authorities. Your documents are secure,
                tamper-proof, and accessible 24/7.
              </Typography>
            </Box>
          </Stack>
        </Paper>

        {/* Ownership Certificates grid */}
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: "#111827", mb: 0.3 }}
          >
            Ownership Certificates
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: "#6b7280", mb: 2, display: "block" }}
          >
            Authority-verified digital ownership certificates
          </Typography>

          <Grid container spacing={3}>
            {ownershipCertificates.map((item, idx) => (
              <Grid item xs={12} md={6} key={idx}>
                <OwnershipCard item={item} onView={handleView} />
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* KYC Verification */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            border: "1px solid #e5e7eb",
            bgcolor: "#ffffff",
            mb: 3,
          }}
        >
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="body2"
              sx={{ fontWeight: 600, color: "#111827", mb: 0.3 }}
            >
              KYC Verification
            </Typography>
            <Typography variant="caption" sx={{ color: "#6b7280" }}>
              Identity verification and compliance status
            </Typography>
          </Box>

          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              bgcolor: "#ecfdf3",
              border: "1px solid #bbf7d0",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Stack direction="row" spacing={2} alignItems="flex-start">
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  bgcolor: "#dcfce7",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CheckCircleOutlineIcon
                  sx={{ fontSize: 20, color: "#16a34a" }}
                />
              </Box>
              <Box>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "#166534", mb: 0.5 }}
                >
                  KYC Verification Report
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: "#166534", display: "block" }}
                >
                  Method:&nbsp; UAE Pass Digital Identity
                </Typography>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mt: 0.5 }}
                >
                  <AccessTimeIcon sx={{ fontSize: 14, color: "#16a34a" }} />
                  <Typography variant="caption" sx={{ color: "#166534" }}>
                    Verified on 10 May 2025
                  </Typography>
                </Stack>
              </Box>
            </Stack>

            <Stack direction="row" spacing={1.5} alignItems="center">
              <Chip
                label="Approved by VARA"
                size="small"
                sx={{
                  borderRadius: 999,
                  bgcolor: "#22c55e",
                  color: "#f9fafb",
                  fontSize: 11,
                }}
              />
              <Button
                variant="contained"
                size="small"
                startIcon={<CloudDownloadOutlinedIcon />}
                sx={{
                  textTransform: "none",
                  borderRadius: 999,
                  fontSize: 12,
                  bgcolor: "#16a34a",
                  "&:hover": { bgcolor: "#15803d" },
                }}
              >
                Download Report
              </Button>
            </Stack>
          </Paper>
        </Paper>

        {/* Payment Receipts */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            border: "1px solid #e5e7eb",
            bgcolor: "#ffffff",
            mb: 3,
          }}
        >
          <Box sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <PaidIcon sx={{ fontSize: 18, color: "#0ea5e9" }} />
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "#111827" }}
              >
                Payment Receipts
              </Typography>
            </Stack>
            <Typography variant="caption" sx={{ color: "#6b7280" }}>
              Transaction receipts and escrow confirmations
            </Typography>
          </Box>

          {receipts.map((item, idx) => (
            <ReceiptRow
              key={idx}
              item={item}
              highlighted={idx === 8} // corporate bond row highlighted like figma
            />
          ))}
        </Paper>

        {/* Compliance Notices */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            borderRadius: 3,
            border: "1px solid #e5e7eb",
            bgcolor: "#ffffff",
            mb: 4,
          }}
        >
          <Box sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <InfoOutlinedIcon sx={{ fontSize: 18, color: "#0ea5e9" }} />
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, color: "#111827" }}
              >
                Compliance Notices
              </Typography>
            </Stack>
            <Typography variant="caption" sx={{ color: "#6b7280" }}>
              Audit reports and regulatory updates
            </Typography>
          </Box>

          <Stack spacing={2.5}>
            {notices.map((item, idx) => (
              <NoticeRow key={idx} item={item} />
            ))}
          </Stack>
        </Paper>
      </Box>
      <OwnershipCertificateModal
        open={purchaseModalOpen}
        onClose={() => setPurchaseModalOpen(false)}
        data={selectedItem}
      />
    </Box>
  );
};

export default DocumentsCertificates;
